import {
  isChatV8PayloadKind,
  z_ChatV8MessagePartMarkdownPayload,
  z_ChatV8UserTextPayload,
} from "@lattice/core-lib/types/chat-v8";
import {
  buildTranscriptRows,
  extractFollowUpPrompts,
  getLatestChatV8ElicitationAnswer,
  isChatV8ThreadRoutePending,
  isCompanyAnalysisElicitation,
  type T_ChatV8RenderableItem,
} from "@lattice/shared-frontend/chat-v8";
import { ArrowDown } from "lucide-react";
import React, { type ReactNode, useMemo } from "react";
import "../chat-v8.css";

import type { T_UseChatV8Result } from "../use-chat-v8";
import { useWebTranscriptScroll } from "../use-transcript-scroll";
import {
  ElicitationChips,
  type T_ElicitationChipsProps,
} from "./ElicitationChips";
import { PromptChipRow } from "./PromptChipRow";
import { PrismTextBody } from "./PrismTextBody";
import { UserAttachmentStrip } from "./UserAttachmentStrip";
import { PlacedWidgetItem } from "./widgets/PlacedWidgetItem";

const STREAMDOWN_WORD_ANIMATION = {
  animation: "fadeIn",
  duration: 120,
  sep: "word",
} as const;

export type T_ConversationTranscriptProps = {
  chat: Pick<
    T_UseChatV8Result,
    | "handleAnswerElicitation"
    | "handleLoadOlder"
    | "loadOlderPending"
    | "renderableItems"
    | "state"
  >;
  emptyContent?: ReactNode;
  expectedThreadCuid: string | null;
  /**
   * Sends a suggested follow-up as a new turn in this thread. Omitted in
   * stories/read-only surfaces, where the pills are simply not rendered.
   */
  onSendFollowUp?: (prompt: string) => void;
  /**
   * Overridable stock search for the company-analysis elicitation. Production
   * uses the live search; stories inject a stub so the transcript renders
   * without a tRPC provider.
   */
  renderStockSearch?: T_ElicitationChipsProps["renderStockSearch"];
};

export function ConversationTranscript({
  chat,
  emptyContent,
  expectedThreadCuid,
  onSendFollowUp,
  renderStockSearch,
}: T_ConversationTranscriptProps) {
  const rows = useMemo(
    () => buildTranscriptRows(chat.renderableItems),
    [chat.renderableItems],
  );
  const error = chat.state.error ?? chat.state.subscriptionError;
  const routeIsPending = isChatV8ThreadRoutePending({
    expectedThreadCuid,
    state: chat.state,
  });
  const {
    contentRef,
    hasContentAbove,
    isAwayFromBottom,
    jumpToBottom,
    notifyScroll,
    notifyUserScrollIntent,
    scrollOwnerRef,
  } = useWebTranscriptScroll({ status: chat.state.status });

  if (routeIsPending) return <TranscriptLoading />;

  return (
    <div className="chat-v8-transcript-region">
      {hasContentAbove ? (
        <div
          aria-hidden
          className="chat-v8-scroll-fade chat-v8-scroll-fade--top"
        />
      ) : null}
      {isAwayFromBottom ? (
        <button
          aria-label="Scroll to latest messages"
          className="chat-v8-jump-to-bottom"
          onClick={jumpToBottom}
          type="button"
        >
          <ArrowDown aria-hidden size={18} />
        </button>
      ) : null}
      <section
        aria-label="Prism conversation"
        className="chat-v8-transcript-scroll"
        onPointerDown={notifyUserScrollIntent}
        onScroll={notifyScroll}
        onTouchStart={notifyUserScrollIntent}
        onWheel={notifyUserScrollIntent}
        ref={scrollOwnerRef}
      >
        <div className="chat-v8-transcript" ref={contentRef}>
          {chat.state.hasMoreBefore ? (
            <button
              className="chat-v8-load-more"
              disabled={chat.loadOlderPending}
              onClick={() => void chat.handleLoadOlder()}
              type="button"
            >
              {chat.loadOlderPending ? "Loading…" : "Load earlier messages"}
            </button>
          ) : null}
          {rows.length === 0 && expectedThreadCuid === null
            ? (emptyContent ?? (
                <div className="chat-v8-empty">
                  <p>What would you like to understand?</p>
                  <span>
                    Ask Prism about markets, companies, or your portfolio.
                  </span>
                </div>
              ))
            : null}
          {rows.map((row) => (
            <TranscriptItem
              chat={chat}
              item={row.item}
              key={row.key}
              onSendFollowUp={onSendFollowUp}
              renderStockSearch={renderStockSearch}
              showHeader={row.showPrismHeader}
            />
          ))}
          {chat.state.status === "sending" ||
          chat.state.status === "streaming" ? (
            <p aria-live="polite" className="chat-v8-thinking">
              Prism is thinking<span aria-hidden>…</span>
            </p>
          ) : null}
          {error ? (
            <div className="chat-v8-error" role="alert">
              <strong>Prism hit a problem.</strong>
              <p>{error}</p>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function TranscriptLoading() {
  return (
    <section
      aria-label="Prism conversation"
      className="chat-v8-transcript-scroll"
    >
      <div
        aria-live="polite"
        className="chat-v8-transcript-loading"
        role="status"
      >
        <span aria-hidden className="chat-v8-transcript-loading__indicator" />
        <span>Loading conversation…</span>
      </div>
    </section>
  );
}

function TranscriptItem({
  chat,
  item,
  onSendFollowUp,
  renderStockSearch,
  showHeader,
}: {
  chat: T_ConversationTranscriptProps["chat"];
  item: T_ChatV8RenderableItem;
  onSendFollowUp?: (prompt: string) => void;
  renderStockSearch?: T_ElicitationChipsProps["renderStockSearch"];
  showHeader: boolean;
}) {
  if (item.kind === "user_text") {
    const userText = z_ChatV8UserTextPayload.safeParse(item.payload);
    const attachments = userText.success
      ? (userText.data.attachments ?? [])
      : [];
    return (
      <div className="chat-v8-user-turn">
        {attachments.length > 0 ? (
          <UserAttachmentStrip
            attachments={attachments}
            itemCuid={item.itemCuid}
          />
        ) : null}
        <div className="chat-v8-user-bubble chat-v8-user-bubble--in-turn">
          {item.text}
        </div>
      </div>
    );
  }
  if (
    item.kind === "message_part" &&
    isChatV8PayloadKind(item.payload, "message_part")
  ) {
    if (item.payload.partKind === "widget")
      return <PlacedWidgetItem item={item} />;
    const markdown = z_ChatV8MessagePartMarkdownPayload.safeParse(item.payload);
    if (!markdown.success || markdown.data.messagePartRole === "thinking")
      return null;
    // Suggested follow-ups arrive as a trailing prose line; lift them out so
    // they render as tappable pills instead of text the user must retype
    // (RAI-1095). Withheld while streaming so pills cannot appear mid-token.
    const followUp = extractFollowUpPrompts(markdown.data.markdown);
    const showFollowUps =
      !item.isLive && followUp.prompts.length > 0 && onSendFollowUp;
    return (
      <article className="chat-v8-assistant">
        {showHeader ? <p className="chat-v8-prism-label">Prism</p> : null}
        <PrismTextBody
          animated={
            shouldAnimateChatV8Markdown(item)
              ? STREAMDOWN_WORD_ANIMATION
              : undefined
          }
          isStreaming={item.isLive}
          markdown={showFollowUps ? followUp.markdown : markdown.data.markdown}
        />
        {showFollowUps ? (
          <PromptChipRow onPress={onSendFollowUp} prompts={followUp.prompts} />
        ) : null}
      </article>
    );
  }
  if (item.kind === "elicitation")
    return (
      <ElicitationChips
        answered={getLatestChatV8ElicitationAnswer(item)}
        onAnswer={(answer) => void chat.handleAnswerElicitation(item, answer)}
        payload={item.payload}
        renderStockSearch={renderStockSearch}
        stockAutocomplete={isCompanyAnalysisElicitation(item)}
      />
    );
  if (item.kind === "tool_card")
    return (
      <p className="chat-v8-thinking">
        {isChatV8PayloadKind(item.payload, "tool_card")
          ? item.payload.title
          : "Prism is working"}
      </p>
    );
  return (
    <p className="chat-v8-fallback">
      Prism shared an item that this version cannot display yet.
    </p>
  );
}

export function shouldAnimateChatV8Markdown(
  item: T_ChatV8RenderableItem,
): boolean {
  return item.events.some(
    (event) =>
      isChatV8PayloadKind(event.payload, "message_part") &&
      "live" in event.payload &&
      event.payload.live === true,
  );
}
